import React, { Fragment, useState, Suspense, useEffect } from 'react';
import { Formik } from 'formik';
import { ProjectConfig, SaveItrConfigPayload, SaveItrConfigInput, ProjectItrConfig } from '@bechtel/types';
import {
  Box,
  Text,
  ButtonAppearance,
  Spinner,
  SpinnerSize,
  ToastType,
  ToastAction,
  useToastsDispatch,
} from '@bechtel/gds';
import { GET_PROJECT_ITR_CONFIG, SAVE_ITR_CONFIG } from '@bechtel/client-gql';
import { NetworkStatus, useQuery, useMutation } from '@apollo/client';
import { useAppUser, ErrorRetry, SuspenseLoader, ErrorBoundary } from '@bechtel/client-common';
import { SaveItrConfigErrorExt } from './error/types';
import {
  ProjectAdminContainerStyled,
  ProjectAdminSubContainerStyled,
  FooterStyled,
  FooterButtonStyled,
} from '../../styled';
import { flatMap, get, map, filter, some } from 'lodash';
import { ItrConfigItem } from './item';
import { FormikState, ItrConfig } from './types';
import { milestoneNoneType } from './form-data';
import ItrConfigError from '~/quantity-reporting/inspection-test-record/error';
import { t, Trans } from '@lingui/macro';

const ItrConfigUpdateToast = () => {
  return (
    <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        <Trans>Inspection Test Record Certification data saved successfully.</Trans>
      </Text>
    </Box>
  );
};

let formikInitialValues: FormikState = {
  itrConfigList: [],
};

export const InspectionTestRecord = () => {
  const { user } = useAppUser();
  const [initialValues, setInitialValues] = useState<FormikState>(formikInitialValues);
  const dispatchToast = useToastsDispatch();
  const [saveItrConfigError, setSaveItrConfigError] = useState<SaveItrConfigErrorExt | null>();
  const { loading, error, data, refetch, networkStatus } = useQuery<{
    projectConfig: ProjectConfig;
  }>(GET_PROJECT_ITR_CONFIG, {
    variables: { id: user?.project?.id },
    notifyOnNetworkStatusChange: true,
  });
  const projectConfigData: ProjectConfig = get(data, 'projectConfigById', undefined);
  const projectItrConfigList: ProjectItrConfig[] = get(projectConfigData, 'projectItrConfig', []);

  useEffect(() => {
    if (!projectConfigData || !projectConfigData.projectItrConfig) return;
    const projectCommodityTypeItrs = flatMap(projectConfigData.projectItrConfig, (item: ProjectItrConfig) => {
      return item.projectCommodityTypeItrs;
    });
    const ItrConfigData: ItrConfig[] =
      projectCommodityTypeItrs.length > 0
        ? projectCommodityTypeItrs.map(({ id, projectMilestoneType }) => ({
            projectCommodityTypeItrId: id,
            projectMilestoneType: projectMilestoneType ? projectMilestoneType : milestoneNoneType,
          }))
        : [];
    formikInitialValues = {
      itrConfigList: [...ItrConfigData],
    };
    setInitialValues(formikInitialValues);
  }, [projectConfigData]);

  const [saveItrConfig, { loading: mutationLoading }] = useMutation<
    { saveItrConfigPayload: SaveItrConfigPayload },
    { input: SaveItrConfigInput[] }
  >(SAVE_ITR_CONFIG, {
    onCompleted: (data) => {
      const itrSaveError = get(data, 'saveItrConfig.error');
      if (itrSaveError) {
        setSaveItrConfigError(itrSaveError);
      } else {
        setSaveItrConfigError(null);
        const toastAction: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: ItrConfigUpdateToast,
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(toastAction);
      }
    },
    onError: (error) => {
      setSaveItrConfigError(error);
    },
  });

  const closeModal = () => {
    setSaveItrConfigError(null);
  };

  return (
    <Fragment>
      <ProjectAdminContainerStyled style={{ overflow: 'hidden' }}>
        {(loading || networkStatus === NetworkStatus.refetch) && (
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
            data-testid="itr-loading"
          >
            <Spinner size={SpinnerSize.LARGE} />
          </Box>
        )}
        {!loading && error && (
          <div data-testid="itr-error-retry">
            <ErrorRetry refetch={refetch}>
              <Trans>
                There was a problem loading Inspection Test Record data. Please click the button below to try again.
              </Trans>
            </ErrorRetry>
          </div>
        )}

        {!loading && !error && projectItrConfigList.length > 0 && (
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={async (values) => {
              const modifiedItems = filter(values.itrConfigList, (item) => {
                return some(initialValues.itrConfigList, (defaultItem) => {
                  return (
                    defaultItem.projectCommodityTypeItrId === item.projectCommodityTypeItrId &&
                    defaultItem.projectMilestoneType !== item.projectMilestoneType
                  );
                });
              });
              const input: SaveItrConfigInput[] = modifiedItems.map(
                ({ projectCommodityTypeItrId, projectMilestoneType }) => ({
                  projectCommodityTypeItrId,
                  projectMilestoneTypeId:
                    projectMilestoneType.id === milestoneNoneType.id ? null : projectMilestoneType.id,
                })
              );
              await saveItrConfig({
                variables: { input },
              });
            }}
          >
            {(formikProps) => {
              const { dirty, isValid, handleReset, handleSubmit } = formikProps;
              return (
                <Fragment>
                  <ProjectAdminSubContainerStyled data-testid="itr-container">
                    <Box
                      p={{ sm: '550' }}
                      flexGrow={{ sm: 1 }}
                      flexDirection={{ sm: 'column' }}
                      css={`
                        overflow-y: auto;
                      `}
                    >
                      <Box mb={{ sm: '400' }}>
                        <Text size="500" family="graphik" weight="600" color="grey.1000">
                          <Trans>Inspection Test Record Certification</Trans>
                        </Text>
                      </Box>
                      {map(projectItrConfigList, (projectItrConfigItem) => {
                        return (
                          <ItrConfigItem
                            formikProps={formikProps}
                            key={projectItrConfigItem.id}
                            projectItrConfig={projectItrConfigItem}
                          />
                        );
                      })}
                    </Box>
                  </ProjectAdminSubContainerStyled>
                  {!loading && !error && (
                    <FooterStyled>
                      <Box>
                        <Box mr={{ sm: '400' }}>
                          <FooterButtonStyled
                            data-testid="itr-cancel"
                            disabled={mutationLoading || !dirty}
                            onClick={() => {
                              handleReset();
                            }}
                            appearance={ButtonAppearance.SECONDARY}
                          >
                            <Trans>Discard Changes</Trans>
                          </FooterButtonStyled>
                        </Box>
                        <FooterButtonStyled
                          data-testid="itr-save"
                          onClick={() => {
                            handleSubmit();
                          }}
                          disabled={mutationLoading || !dirty || !isValid}
                          appearance={ButtonAppearance.SECONDARY}
                        >
                          {mutationLoading && <Spinner />}
                          {!mutationLoading && t`Save Changes`}
                        </FooterButtonStyled>
                      </Box>
                    </FooterStyled>
                  )}
                </Fragment>
              );
            }}
          </Formik>
        )}
      </ProjectAdminContainerStyled>
      {saveItrConfigError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <ItrConfigError error={saveItrConfigError} close={closeModal} />
          </Suspense>
        </ErrorBoundary>
      )}
    </Fragment>
  );
};
